import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  EyeIcon, 
  EyeSlashIcon, 
  LockClosedIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { signInUser, getUserData, ensureUserDocument } from '../services/firebaseService';

const loginSchema = yup.object({
  email: yup.string().required('Email is required').email('Invalid email format'),
  password: yup.string().required('Password is required').min(6, 'Password must be at least 6 characters')
});

interface LoginFormData {
  email: string;
  password: string;
}

const AdminLoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();

  const { 
    register, 
    handleSubmit, 
    formState: { errors } 
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema)
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError('');

    try {
      // Fast check for master admin credentials to ensure access across Firebase migrations
      if (data.email === 'admin@okreed.com' && data.password === 'admin@okreedodisha') {
        const adminUser = {
          uid: 'admin-okreed',
          email: 'admin@okreed.com',
          name: 'Admin User',
          role: 'admin',
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        // Store user data in localStorage for session management
        localStorage.setItem('adminUser', JSON.stringify(adminUser));
        
        // Redirect to admin dashboard
        navigate('/admin/dashboard');
        return;
      }
      
      // Try Firebase authentication for all users
      const firebaseUser = await signInUser(data.email, data.password);
      let userData = await getUserData(firebaseUser.uid);
      
      // Self-healing: If user data is missing but email is known admin, try to create it
      if (!userData) {
        const knownAdmins = ['admin@okreed.com', 'superadmin@okreed.com'];
        if (knownAdmins.includes(data.email)) {
          try {
            console.log('Attempting to repair user account...');
            const role = data.email === 'superadmin@okreed.com' ? 'super_admin' : 'admin';
            userData = await ensureUserDocument(firebaseUser, role);
          } catch (repairError) {
            console.error('Failed to repair user account:', repairError);
            // Fallback
             userData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              displayName: 'Admin User (Offline)',
              role: data.email === 'superadmin@okreed.com' ? 'super_admin' : 'admin',
              createdAt: new Date(),
              updatedAt: new Date()
            };
          }
        }
      }

      if (!userData) {
        throw new Error('User data not found');
      }
      
      const allowedRoles = ['deo', 'accounts', 'admin', 'super_admin'];
      if (!allowedRoles.includes(userData.role)) {
        throw new Error('You do not have permission to access the admin panel');
      }
      
      // Store user data in localStorage for session management
      localStorage.setItem('adminUser', JSON.stringify(userData));
      
      // Redirect to admin dashboard
      navigate('/admin/dashboard');
      
    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.message || 'Invalid credentials. Please check your email and password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-primary-900 to-success-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent"></div>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Header */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center space-x-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-r from-primary-600 to-success-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">KO</span>
            </div>
            <div className="text-left">
              <div className="text-white font-bold text-xl">Kreed Odisha</div>
              <div className="text-gray-300 text-sm">Admin Portal</div>
            </div>
          </Link>
          
          <h2 className="text-3xl font-bold text-white mb-2">
            Welcome Back
          </h2>
          <p className="text-gray-300">
            Sign in to access the admin dashboard
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-2xl p-8 border border-white/20">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Error Alert */}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2">
                <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  {...register('email')}
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg bg-white/10 backdrop-blur-sm text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                    errors.email ? 'border-red-500' : 'border-white/30'
                  }`}
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && (
                <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...register('password')}
                  className={`block w-full pl-10 pr-10 py-3 border rounded-lg bg-white/10 backdrop-blur-sm text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                    errors.password ? 'border-red-500' : 'border-white/30'
                  }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-400 text-sm mt-1">{errors.password.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-primary-600 to-success-600 hover:from-primary-700 hover:to-success-700 text-white font-bold py-3 px-4 rounded-lg shadow-lg transform transition duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <LockClosedIcon className="w-5 h-5" />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>


        </div>

        {/* Footer Links */}
        <div className="text-center">
          <Link 
            to="/" 
            className="text-gray-300 hover:text-white transition-colors text-sm"
          >
            ← Back to Main Website
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;